import React, { useMemo, useState } from 'react';
import { Input } from '@/ui-kit/input';
import { cn } from '@/lib/utils';

/** ~4 visible rows of pill chips (text-xs + py-0.5 + gap-1). */
export const ONBOARDING_SECTION_SCROLL_MIN_HEIGHT = '8.5rem';

export interface OnboardingOption {
  value: string;
  count: number;
}

interface OnboardingAttributePickerProps {
  options: OnboardingOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  /** Section layout: fixed min height (~4 chip rows), scroll inside; no search field. */
  layout?: 'default' | 'section';
}

/** Pill styling aligned with ProductCardModal renderSelectableValue. */
function chipClassName(selected: boolean): string {
  return cn(
    'inline-block px-2 py-0.5 rounded-full cursor-pointer transition-all text-xs',
    'max-w-[220px] truncate align-middle',
    selected
      ? 'bg-primary text-primary-foreground ring-2 ring-primary/40'
      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:ring-1 hover:ring-primary/50'
  );
}

export const OnboardingAttributePicker: React.FC<OnboardingAttributePickerProps> = ({
  options,
  selected,
  onChange,
  isLoading = false,
  searchPlaceholder = 'Search options…',
  layout = 'default',
}) => {
  const [search, setSearch] = useState('');
  const isSection = layout === 'section';

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => a.value.localeCompare(b.value));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (isSection) return sortedOptions;
    const q = search.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter((o) => o.value.toLowerCase().includes(q));
  }, [sortedOptions, search, isSection]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const chipList = (
    <div className="flex flex-wrap gap-1 content-start">
      {filteredOptions.map(({ value }) => {
        const isSelected = selected.includes(value);
        return (
          <button
            key={value}
            type="button"
            role="option"
            aria-selected={isSelected}
            title={value}
            className={chipClassName(isSelected)}
            onClick={() => toggle(value)}
          >
            {value}
          </button>
        );
      })}
    </div>
  );

  if (isLoading) {
    return (
      <div
        className={cn(
          'text-xs text-muted-foreground flex items-center justify-center rounded-md border border-border/60 bg-background/50',
          isSection && 'min-h-[8.5rem]'
        )}
      >
        Loading…
      </div>
    );
  }

  if (sortedOptions.length === 0) {
    return (
      <p
        className={cn(
          'text-sm text-muted-foreground rounded-md border border-border/60 bg-background/50 p-3',
          isSection && 'min-h-[8.5rem] flex items-center'
        )}
      >
        No options available for this period. Skip this category if you like.
      </p>
    );
  }

  const scrollBoxClass = isSection
    ? cn(
        'overflow-y-auto rounded-md border border-border/60 bg-background/50 p-2',
        'min-h-[8.5rem] max-h-[8.5rem]'
      )
    : cn(
        'flex-1 min-h-[200px] max-h-[min(360px,45vh)] overflow-y-auto rounded-md border border-border/60 bg-background/50 p-2'
      );

  return (
    <div className={cn('flex flex-col gap-2', !isSection && 'gap-3 min-h-0 flex-1')}>
      {!isSection && (
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 shrink-0"
        />
      )}
      <div className={scrollBoxClass} role="listbox" aria-multiselectable>
        {chipList}
        {!isSection && filteredOptions.length === 0 && (
          <p className="text-xs text-muted-foreground px-1 py-2">No matches for your search.</p>
        )}
      </div>
      <p className="text-xs text-muted-foreground shrink-0">
        {selected.length > 0
          ? `${selected.length} selected`
          : isSection
            ? 'None selected — optional'
            : 'Nothing selected for this step — that is fine.'}
      </p>
    </div>
  );
};
